import React, { useEffect, useState } from 'react';
import axios from '../axios';
import { useTranslation } from 'react-i18next';
import { useToast } from '../components/ToastProvider';
import StatusBadge from '../components/StatusBadge';
import MobileDataCard from '../components/MobileDataCard';
import { formatDate, formatDateTime } from '../utils/formatters';

interface DashboardData {
  newLeads: number;
  dealsInNegotiation: number;
  payoutRequests: number;
}

interface Lead {
  id: string;
  companyName: string;
  domain: string;
  partnerEmail: string;
  status: 'SUBMITTED' | 'ACCEPTED' | 'REJECTED' | 'DELETED';
  contactSource: 'PERSONAL' | 'BUSINESS';
  createdAt: string;
}

interface LeadDetail extends Lead {
  companyStatus: string;
  contactName: string;
  contactEmail: string;
  contactPhone?: string;
  partnerPhoneNumber?: string;
  salesTip?: string;
  closerComment?: string;
}

interface LeadFormState {
  companyName: string;
  domain: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  contactSource: 'PERSONAL' | 'BUSINESS';
  salesTip: string;
  closerComment: string;
  status: 'SUBMITTED' | 'ACCEPTED' | 'REJECTED' | 'DELETED';
}

const EMPTY_FORM: LeadFormState = {
  companyName: '',
  domain: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  contactSource: 'BUSINESS',
  salesTip: '',
  closerComment: '',
  status: 'SUBMITTED',
};

const AdminDashboard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<DashboardData | null>(null);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [selectedLead, setSelectedLead] = useState<LeadDetail | null>(null);
  const [loadingLead, setLoadingLead] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit' | null>(null);
  const [formTargetId, setFormTargetId] = useState<string | null>(null);
  const [formState, setFormState] = useState<LeadFormState>(EMPTY_FORM);
  const toast = useToast();

  const fetchData = async () => {
    const [dashboard, leadsResponse] = await Promise.all([
      axios.get('/api/admin/dashboard'),
      axios.get('/api/admin/leads'),
    ]);
    setData(dashboard.data);
    setLeads(leadsResponse.data);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const loadLeadDetail = async (id: string) => {
    const res = await axios.get(`/api/admin/leads/${id}`);
    return res.data as LeadDetail;
  };

  const openLeadDetail = async (id: string) => {
    try {
      setLoadingLead(true);
      setSelectedLead(await loadLeadDetail(id));
    } catch (e: any) {
      toast.show(String(e?.response?.data || t('admin.leads.loadError')), 'error');
    } finally {
      setLoadingLead(false);
    }
  };

  const openCreateForm = () => {
    setFormMode('create');
    setFormTargetId(null);
    setFormState(EMPTY_FORM);
  };

  const openEditForm = (lead: LeadDetail) => {
    setSelectedLead(null);
    setFormMode('edit');
    setFormTargetId(lead.id);
    setFormState({
      companyName: lead.companyName,
      domain: lead.domain,
      contactName: lead.contactName,
      contactEmail: lead.contactEmail,
      contactPhone: lead.contactPhone || '',
      contactSource: lead.contactSource,
      salesTip: lead.salesTip || '',
      closerComment: lead.closerComment || '',
      status: lead.status,
    });
  };

  const handleLeadAction = async (action: 'accept' | 'reject') => {
    if (!selectedLead) return;
    try {
      setBusy(`${action}:${selectedLead.id}`);
      await axios.post(`/api/admin/leads/${selectedLead.id}/${action}`);
      toast.show(t(action === 'accept' ? 'admin.leads.accepted' : 'admin.leads.rejected') as string, 'success');
      setSelectedLead(null);
      fetchData();
    } catch (e: any) {
      toast.show(String(e?.response?.data || t('admin.leads.actionError')), 'error');
    } finally {
      setBusy(null);
    }
  };

  const submitLeadForm = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setBusy('save-lead');
      if (formMode === 'create') {
        await axios.post('/api/admin/leads', formState);
        toast.show(t('admin.leads.created') as string, 'success');
      } else if (formMode === 'edit' && formTargetId) {
        await axios.patch(`/api/admin/leads/${formTargetId}`, formState);
        toast.show(t('admin.leads.updated') as string, 'success');
      }
      setFormMode(null);
      setFormTargetId(null);
      setFormState(EMPTY_FORM);
      fetchData();
    } catch (e: any) {
      toast.show(String(e?.response?.data || t('admin.leads.saveError')), 'error');
    } finally {
      setBusy(null);
    }
  };

  const deleteLead = async (leadId: string) => {
    if (!window.confirm(t('admin.leads.deleteConfirm') as string)) {
      return;
    }
    try {
      setBusy(`delete:${leadId}`);
      await axios.delete(`/api/admin/leads/${leadId}`);
      toast.show(t('admin.leads.deleted') as string, 'success');
      if (selectedLead?.id === leadId) {
        setSelectedLead(null);
      }
      fetchData();
    } catch (e: any) {
      toast.show(String(e?.response?.data || t('admin.leads.deleteError')), 'error');
    } finally {
      setBusy(null);
    }
  };

  const stats = [
    { label: t('admin.newLeads'), value: data?.newLeads ?? 0 },
    { label: t('admin.dealsInNegotiation'), value: data?.dealsInNegotiation ?? 0 },
    { label: t('admin.pendingPayouts'), value: data?.payoutRequests ?? 0 },
  ];

  return (
    <section>
      <h1 className="text-2xl md:text-3xl font-bold mb-6 text-[#111D4E]">{t('admin.title')}</h1>
      <div className="flex justify-end mb-4">
        <button onClick={fetchData} className="px-3 py-1 bg-[#47ABDB] text-white rounded text-sm">{t('common.refresh')}</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {stats.map((stat) => (
          <div key={stat.label} className="bg-white/90 text-gray-800 rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden">
            <div className="px-4 py-3 bg-gradient-to-r from-[#E9F4FB] via-white to-[#F4F8FB]">
              <p className="text-xs font-semibold text-[#111D4E] uppercase tracking-wide">{stat.label}</p>
            </div>
            <div className="px-5 py-5">
              <p className="text-3xl font-semibold text-[#111D4E]">{stat.value}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white/90 text-gray-800 rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-4 bg-gradient-to-r from-[#E9F4FB] via-white to-[#F4F8FB]">
          <div>
            <div className="text-sm font-semibold text-[#111D4E]">{t('admin.leads.title')}</div>
            <div className="text-xs text-gray-600">{t('admin.leads.subtitle')}</div>
          </div>
          <div className="flex gap-2">
            <button onClick={openCreateForm} className="px-4 py-2 bg-white text-[#111D4E] border border-slate-200 rounded-md text-sm font-semibold shadow-sm hover:bg-slate-50">
              {t('admin.leads.new')}
            </button>
            <button onClick={fetchData} className="px-4 py-2 bg-[#47ABDB] text-white rounded-md text-sm font-semibold shadow-sm hover:bg-[#3d8fb8]">
              {t('common.refresh')}
            </button>
          </div>
        </div>
        <div className="space-y-3 p-4 md:hidden">
          {leads.length === 0 ? (
            <div className="rounded-2xl border border-slate-200/80 bg-white px-5 py-10 text-center text-base text-gray-500">
              {t('admin.leads.empty')}
            </div>
          ) : (
            leads.map((lead) => (
              <MobileDataCard
                key={lead.id}
                title={lead.companyName}
                badge={<StatusBadge kind="lead" status={lead.status} />}
                subtitle={lead.domain}
                rows={[
                  { label: t('admin.leads.submittedBy'), value: lead.partnerEmail },
                  { label: t('submitLead.contactSource'), value: t(`submitLead.contactSourceValue.${lead.contactSource}`) },
                  { label: t('admin.leads.createdAt'), value: formatDate(lead.createdAt, i18n.resolvedLanguage) },
                ]}
                actions={
                  <>
                    <button onClick={() => openLeadDetail(lead.id)} disabled={loadingLead} className="w-full px-4 py-2 bg-[#47ABDB] disabled:opacity-60 text-white rounded-md text-sm font-semibold">
                      {t('admin.leads.view')}
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          setLoadingLead(true);
                          openEditForm(await loadLeadDetail(lead.id));
                        } catch (e: any) {
                          toast.show(String(e?.response?.data || t('admin.leads.loadError')), 'error');
                        } finally {
                          setLoadingLead(false);
                        }
                      }}
                      className="w-full px-4 py-2 bg-slate-200 text-slate-700 rounded-md text-sm font-semibold"
                    >
                      {t('admin.leads.edit')}
                    </button>
                    <button onClick={() => deleteLead(lead.id)} disabled={lead.status === 'DELETED' || busy === `delete:${lead.id}`} className="w-full px-4 py-2 bg-red-100 text-red-700 rounded-md text-sm font-semibold disabled:opacity-60">
                      {busy === `delete:${lead.id}` ? t('common.loading') : t('admin.leads.delete')}
                    </button>
                  </>
                }
              />
            ))
          )}
        </div>
        <div className="hidden overflow-x-auto md:block">
          <table className="min-w-full text-left table-auto text-[15px] md:text-base">
            <thead className="bg-[#F3F6F9] text-sm uppercase tracking-wide text-[#111D4E]">
              <tr>
                <th className="px-5 py-3 font-semibold">{t('submitLead.company')}</th>
                <th className="px-5 py-3 font-semibold">{t('submitLead.domain')}</th>
                <th className="px-5 py-3 font-semibold">{t('admin.leads.submittedBy')}</th>
                <th className="px-5 py-3 font-semibold">{t('submitLead.contactSource')}</th>
                <th className="px-5 py-3 font-semibold">{t('admin.leads.status')}</th>
                <th className="px-5 py-3 font-semibold">{t('admin.leads.createdAt')}</th>
                <th className="px-5 py-3 font-semibold">{t('admin.leads.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-5 py-10 text-center text-base text-gray-500">
                    {t('admin.leads.empty')}
                  </td>
                </tr>
              ) : (
                leads.map((lead, idx) => (
                  <tr key={lead.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-[#FBFCFE]'}>
                    <td className="px-5 py-4 font-medium text-[#111D4E]">{lead.companyName}</td>
                    <td className="px-5 py-4 text-sm">{lead.domain}</td>
                    <td className="px-5 py-4 text-sm">{lead.partnerEmail}</td>
                    <td className="px-5 py-4 text-sm">{t(`submitLead.contactSourceValue.${lead.contactSource}`)}</td>
                    <td className="px-5 py-4">
                      <StatusBadge kind="lead" status={lead.status} />
                    </td>
                    <td className="px-5 py-4">{formatDate(lead.createdAt, i18n.resolvedLanguage)}</td>
                    <td className="px-5 py-4 space-x-2">
                      <button onClick={() => openLeadDetail(lead.id)} disabled={loadingLead} className="px-3 py-1 bg-[#47ABDB] disabled:opacity-60 text-white rounded text-sm">
                        {t('admin.leads.view')}
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            setLoadingLead(true);
                            openEditForm(await loadLeadDetail(lead.id));
                          } catch (e: any) {
                            toast.show(String(e?.response?.data || t('admin.leads.loadError')), 'error');
                          } finally {
                            setLoadingLead(false);
                          }
                        }}
                        className="px-3 py-1 bg-slate-200 text-slate-700 rounded text-sm"
                      >
                        {t('admin.leads.edit')}
                      </button>
                      <button onClick={() => deleteLead(lead.id)} disabled={lead.status === 'DELETED' || busy === `delete:${lead.id}`} className="px-3 py-1 bg-red-100 text-red-700 rounded text-sm disabled:opacity-60">
                        {busy === `delete:${lead.id}` ? t('common.loading') : t('admin.leads.delete')}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedLead && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center px-4 py-4">
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white text-gray-800 rounded-2xl shadow-sm border border-slate-200/80">
            <div className="px-5 py-4 bg-gradient-to-r from-[#E9F4FB] via-white to-[#F4F8FB]">
              <div className="text-sm font-semibold text-[#111D4E]">{t('admin.leads.detailTitle')}</div>
              <div className="text-xs text-gray-600">{t('admin.leads.detailSubtitle')}</div>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div><span className="font-semibold">{t('submitLead.company')}:</span> {selectedLead.companyName}</div>
              <div><span className="font-semibold">{t('submitLead.domain')}:</span> {selectedLead.domain}</div>
              <div><span className="font-semibold">{t('admin.companyStatus')}:</span> <StatusBadge kind="company" status={selectedLead.companyStatus} /></div>
              <div><span className="font-semibold">{t('admin.leads.status')}:</span> <StatusBadge kind="lead" status={selectedLead.status} /></div>
              <div><span className="font-semibold">{t('admin.leads.submittedBy')}:</span> {selectedLead.partnerEmail}</div>
              <div><span className="font-semibold">{t('settings.phone')}:</span> {selectedLead.partnerPhoneNumber || '-'}</div>
              <div><span className="font-semibold">{t('submitLead.contactSource')}:</span> {t(`submitLead.contactSourceValue.${selectedLead.contactSource}`)}</div>
              <div><span className="font-semibold">{t('submitLead.contactName')}:</span> {selectedLead.contactName}</div>
              <div><span className="font-semibold">{t('submitLead.contactEmail')}:</span> {selectedLead.contactEmail}</div>
              <div><span className="font-semibold">{t('submitLead.contactPhone')}:</span> {selectedLead.contactPhone || '-'}</div>
              <div><span className="font-semibold">{t('admin.leads.createdAt')}:</span> {formatDateTime(selectedLead.createdAt, i18n.resolvedLanguage)}</div>
              <div className="md:col-span-2"><span className="font-semibold">{t('submitLead.salesTip')}:</span> {selectedLead.salesTip || '-'}</div>
              <div className="md:col-span-2"><span className="font-semibold">{t('admin.leads.closerComment')}:</span> {selectedLead.closerComment || '-'}</div>
            </div>
            <div className="px-6 pb-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button type="button" onClick={() => setSelectedLead(null)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md">
                {t('common.close')}
              </button>
              <button type="button" onClick={() => openEditForm(selectedLead)} className="px-4 py-2 bg-slate-200 text-slate-700 rounded-md">
                {t('admin.leads.edit')}
              </button>
              <button type="button" onClick={() => deleteLead(selectedLead.id)} disabled={selectedLead.status === 'DELETED'} className="px-4 py-2 bg-red-100 text-red-700 rounded-md disabled:opacity-60">
                {t('admin.leads.delete')}
              </button>
              {selectedLead.status === 'SUBMITTED' && (
                <>
                  <button onClick={() => handleLeadAction('reject')} disabled={busy === `reject:${selectedLead.id}`} className="px-4 py-2 bg-gray-200 disabled:opacity-60 text-gray-700 rounded-md">
                    {busy === `reject:${selectedLead.id}` ? t('common.loading') : t('admin.leads.reject')}
                  </button>
                  <button onClick={() => handleLeadAction('accept')} disabled={busy === `accept:${selectedLead.id}`} className="px-4 py-2 bg-[#47ABDB] disabled:opacity-60 text-white rounded-md">
                    {busy === `accept:${selectedLead.id}` ? t('common.loading') : t('admin.leads.accept')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {formMode && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center px-4 py-4">
          <form onSubmit={submitLeadForm} className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white text-gray-800 rounded-2xl shadow-sm border border-slate-200/80">
            <div className="px-5 py-4 bg-gradient-to-r from-[#E9F4FB] via-white to-[#F4F8FB]">
              <div className="text-sm font-semibold text-[#111D4E]">
                {t(formMode === 'create' ? 'admin.leads.createTitle' : 'admin.leads.editTitle')}
              </div>
              <div className="text-xs text-gray-600">{t('admin.leads.formSubtitle')}</div>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('submitLead.company')}</label>
                <input value={formState.companyName} onChange={(e) => setFormState((prev) => ({ ...prev, companyName: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-md" required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('submitLead.domain')}</label>
                <input value={formState.domain} onChange={(e) => setFormState((prev) => ({ ...prev, domain: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-md" required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('submitLead.contactName')}</label>
                <input value={formState.contactName} onChange={(e) => setFormState((prev) => ({ ...prev, contactName: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-md" required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('submitLead.contactEmail')}</label>
                <input type="email" value={formState.contactEmail} onChange={(e) => setFormState((prev) => ({ ...prev, contactEmail: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-md" required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('submitLead.contactPhone')}</label>
                <input value={formState.contactPhone} onChange={(e) => setFormState((prev) => ({ ...prev, contactPhone: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('submitLead.contactSource')}</label>
                <select value={formState.contactSource} onChange={(e) => setFormState((prev) => ({ ...prev, contactSource: e.target.value as 'PERSONAL' | 'BUSINESS' }))} className="w-full px-3 py-2 border border-slate-200 rounded-md bg-white">
                  <option value="BUSINESS">{t('submitLead.contactSourceBusiness')}</option>
                  <option value="PERSONAL">{t('submitLead.contactSourcePersonal')}</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">{t('submitLead.salesTip')}</label>
                <textarea value={formState.salesTip} onChange={(e) => setFormState((prev) => ({ ...prev, salesTip: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-md" rows={3} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">{t('admin.leads.closerComment')}</label>
                <textarea value={formState.closerComment} onChange={(e) => setFormState((prev) => ({ ...prev, closerComment: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-md" rows={3} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('admin.leads.status')}</label>
                <select value={formState.status} onChange={(e) => setFormState((prev) => ({ ...prev, status: e.target.value as 'SUBMITTED' | 'ACCEPTED' | 'REJECTED' | 'DELETED' }))} className="w-full px-3 py-2 border border-slate-200 rounded-md bg-white">
                  <option value="SUBMITTED">{t('status.lead.SUBMITTED')}</option>
                  {formState.status === 'ACCEPTED' && <option value="ACCEPTED">{t('status.lead.ACCEPTED')}</option>}
                  <option value="REJECTED">{t('status.lead.REJECTED')}</option>
                  {formState.status === 'DELETED' && <option value="DELETED">{t('status.lead.DELETED')}</option>}
                </select>
              </div>
            </div>
            <div className="px-6 pb-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button type="button" onClick={() => setFormMode(null)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md">
                {t('common.cancel')}
              </button>
              <button type="submit" disabled={busy === 'save-lead'} className="px-4 py-2 bg-[#47ABDB] disabled:opacity-60 text-white rounded-md">
                {busy === 'save-lead' ? t('common.loading') : t('common.save')}
              </button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
};

export default AdminDashboard;
